/**
 * Pure logic for the segmented (one-box-per-character) offer-code input.
 *
 * Kept DOM-free so the focus/paste/backspace behaviour is unit-testable; the
 * page handler is a thin layer that maps input events onto these helpers and
 * feeds joinSegments() to the existing validateOfferCode().
 */
export const SEGMENT_COUNT = 10;

const ALPHANUMERIC = /^[A-Z0-9]$/;

/**
 * Zero-width space kept in every empty box. On an empty input the Android IME
 * (Home Hub WebView) sends no event at all for backspace, there is nothing to
 * delete; with the filler there is, so the deletion reaches the input event.
 */
export const EMPTY_BOX_FILLER = "\u200B";

export type SegmentUpdate = {
  segments: string[];
  focusIndex: number;
};

export function emptySegments(): string[] {
  return Array.from({ length: SEGMENT_COUNT }, () => "");
}

function clampIndex(index: number): number {
  return Math.max(0, Math.min(SEGMENT_COUNT - 1, index));
}

function normalizeChar(char: string): string {
  return String(char || "").trim().toUpperCase();
}

/** Enter a single character at `index`, advancing focus to the next box. */
export function applyTypedChar(segments: string[], index: number, char: string): SegmentUpdate {
  const normalizedChar = normalizeChar(char);
  const cursor = clampIndex(index);
  if (!ALPHANUMERIC.test(normalizedChar)) return { segments: segments.slice(), focusIndex: cursor };
  const next = segments.slice();
  next[cursor] = normalizedChar;
  return { segments: next, focusIndex: clampIndex(cursor + 1) };
}

/**
 * Backspace: clear the current box if filled; otherwise clear the previous box
 * and step back — matching how OTP-style inputs behave.
 */
export function applyBackspace(segments: string[], index: number): SegmentUpdate {
  const cursor = clampIndex(index);
  const next = segments.slice();
  if (next[cursor]) {
    next[cursor] = "";
    return { segments: next, focusIndex: cursor };
  }
  const previous = clampIndex(cursor - 1);
  next[previous] = "";
  return { segments: next, focusIndex: previous };
}

/**
 * Distribute pasted text across boxes starting at `index`, stripping spaces and
 * dashes and uppercasing. Extra characters beyond the row are dropped.
 */
export function applyPaste(segments: string[], index: number, text: string): SegmentUpdate {
  const chars = normalizeChar(text)
    .replace(/[\s-]/g, "")
    .split("")
    .filter(char => ALPHANUMERIC.test(char));
  const next = segments.slice();
  let cursor = clampIndex(index);
  for (const char of chars) {
    if (cursor >= SEGMENT_COUNT) break;
    next[cursor] = char;
    cursor += 1;
  }
  return { segments: next, focusIndex: clampIndex(cursor) };
}

/**
 * Handle whatever the box value became after an `input` event, without relying
 * on keydown: Android IMEs report keydown as "Unidentified" (keyCode 229), so
 * the value is the only reliable signal. A value with nothing left once the
 * filler is dropped is a backspace (the character, or the filler of an empty
 * box); a value still holding the previous character is an overwrite, whatever
 * the caret position; several new characters (IME suggestion, autofill) are a
 * paste.
 */
export function applyInputValue(segments: string[], index: number, value: string): SegmentUpdate {
  const cursor = clampIndex(index);
  let typed = String(value || "").split(EMPTY_BOX_FILLER).join("");
  if (!typed) return applyBackspace(segments, cursor);
  const previous = segments[cursor];
  if (previous && typed.length > 1) typed = typed.replace(previous, "");
  if (typed.length > 1) return applyPaste(segments, cursor, typed);
  return applyTypedChar(segments, cursor, typed);
}

/** What a box displays: its character, or the filler when empty. */
export function boxValue(segment: string): string {
  return segment || EMPTY_BOX_FILLER;
}

/** Assemble the entered characters into the code string for validation. */
export function joinSegments(segments: string[]): string {
  return segments.join("");
}
